import {
    afterVersion,
    applyMutation,
    entityKeys,
    findingPhotoSlot,
    newUlid,
    requireTenantDb,
    type FindingRecord,
    type TenantDatabase,
    type WireScope,
} from '@/shared/offline';

import { assertEditable } from '@/entities/inspection';

export interface ManualFindingInput {
    inspectionUuid: string;
    /** Weglaten voor een nieuwe bevinding. */
    findingUuid?: string;
    /** Het onderdeel: de templateknoop en de instanties eromheen; `null` zonder onderdeel. */
    nodeId: number | null;
    multiScope: WireScope;
    title: string | null;
    description: string | null;
    /** Waar het gebrek zit, in een handvol woorden; `null` als er niets staat. */
    location: string | null;
    severity: string | null;
    urgency: string | null;
    directCost: string | null;
    termCost: string | null;
    improvementCost: string | null;
    costGroupId: number | null;
    nhgClassification: string | null;
}

/**
 * Handmatige bevindingen (`source = manual`), de enige die de app mag wijzigen.
 * Bevindingen die uit een antwoord volgen, wijzig je via dat antwoord.
 */
export const findingMutations = {
    async saveManual(input: ManualFindingInput, db: TenantDatabase = requireTenantDb()) {
        const findingUuid = input.findingUuid ?? newUlid();

        return applyMutation(
            {
                apply: async (database, clientUpdatedAt) => {
                    await assertEditable(database, input.inspectionUuid);

                    const existing = await database.findings.get(findingUuid);
                    const at = afterVersion(clientUpdatedAt, existing?.clientUpdatedAt);
                    const record: FindingRecord = {
                        findingUuid,
                        inspectionUuid: input.inspectionUuid,
                        nodeId: input.nodeId,
                        multiScope: input.nodeId === null ? [] : [...input.multiScope],
                        title: input.title,
                        description: input.description,
                        location: input.location,
                        severity: input.severity,
                        urgency: input.urgency,
                        directCost: input.directCost,
                        termCost: input.termCost,
                        improvementCost: input.improvementCost,
                        costGroupId: input.costGroupId,
                        nhgClassification: input.nhgClassification,
                        source: 'manual',
                        clientUpdatedAt: at,
                        confirmed: existing?.confirmed ?? 0,
                    };

                    await database.findings.put(record);

                    return [
                        {
                            body: {
                                entity: 'finding',
                                action: 'upsert',
                                payload: {
                                    inspection_uuid: input.inspectionUuid,
                                    finding_uuid: findingUuid,
                                    node_id: input.nodeId,
                                    multi_scope: input.nodeId === null ? [] : [...input.multiScope],
                                    title: input.title,
                                    description: input.description,
                                    location: input.location,
                                    severity: input.severity,
                                    urgency: input.urgency,
                                    direct_cost: input.directCost,
                                    term_cost: input.termCost,
                                    improvement_cost: input.improvementCost,
                                    cost_group_id: input.costGroupId,
                                    nhg_classification: input.nhgClassification,
                                    source: 'manual',
                                },
                            },
                            inspectionUuid: input.inspectionUuid,
                            entityKey: entityKeys.finding(findingUuid),
                            clientUpdatedAt: at,
                        },
                    ];
                },
            },
            db,
        );
    },

    async removeManual(
        input: { inspectionUuid: string; findingUuid: string },
        db: TenantDatabase = requireTenantDb(),
    ) {
        return applyMutation(
            {
                apply: async (database, clientUpdatedAt) => {
                    await assertEditable(database, input.inspectionUuid);

                    const existing = await database.findings.get(input.findingUuid);

                    if (!existing || existing.source !== 'manual') {
                        return [];
                    }

                    await database.findings.delete(input.findingUuid);
                    /* Haar foto's gaan mee; wat al op de server stond, verwijdert de server met haar. */
                    await database.photos
                        .where('[inspectionUuid+slot]')
                        .equals([input.inspectionUuid, findingPhotoSlot(input.findingUuid)])
                        .delete();

                    return [
                        {
                            body: {
                                entity: 'finding',
                                action: 'delete',
                                payload: {
                                    inspection_uuid: input.inspectionUuid,
                                    finding_uuid: input.findingUuid,
                                },
                            },
                            inspectionUuid: input.inspectionUuid,
                            entityKey: entityKeys.finding(input.findingUuid),
                            /* Nooit op de server geweest: dan heffen upsert en delete elkaar op. */
                            unsynced: existing.confirmed !== 1,
                            clientUpdatedAt: afterVersion(
                                clientUpdatedAt,
                                existing.clientUpdatedAt,
                            ),
                        },
                    ];
                },
            },
            db,
        );
    },
};
